import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import {
  CalendarBlank,
  CaretDown,
  CaretUp,
  CheckCircle,
  CreditCard,
  Repeat,
  Warning,
} from 'phosphor-react';

import { QuickbooksInvoice, formatMoney } from '@dill/dill-shared';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { getGroupedBuyersSuppliers } from '../../../../redux/services/buyersService';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';
import {
  getMainBuyerPaymentPlans,
  cancelMainBuyerPaymentPlan,
  confirmMainBuyerPaymentPlan,
  updateMainBuyerPaymentPlanPaymentMethod,
} from '../../../../redux/services/paymentPlanService';
import { getMainBuyerPaymentMethods } from '../../../../redux/services/paymentsService';
import { DillButton, DillCheckbox, DillField, DillSelect } from '../../../general/Dill';
import { COLORS } from '../../../../utils/colors';
import { openDillToast } from '../../../../utils/helpers';
import { BuyerPaymentMethods } from '../BuyerPaymentMethods/BuyerPaymentMethods';
import { PaymentPlanHistoryModal } from '../../../general/PaymentPlanHistoryModal/PaymentPlanHistoryModal';
import { PaymentPlanHistoryList } from '../../../general/PaymentPlanHistoryModal/PaymentPlanHistoryList';
import AppSelectedPayInvoicesModal from '../../../general/AppSelectedPayInvoicesModal/AppSelectedPayInvoicesModal';
import {
  formatPaymentPlanOccurrence,
  formatPaymentPlanConfirmationTimestamp,
  generatePaymentPlanInstallments,
} from '../../../../utils/paymentPlanHelpers';

const StatTile = ({
  label,
  value,
  accent,
  onClick,
}: {
  label: string;
  value: string;
  accent?: boolean;
  onClick?: () => void;
}) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-xl border p-3 text-left w-full ${
        accent ? 'border-blue-100 bg-blue-50/60' : 'border-gray-100 bg-gray-50'
      } ${onClick ? 'hover:border-blue-300 transition-colors cursor-pointer' : ''}`}>
      <div className="text-2xs font-medium text-gray-500 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${accent ? 'text-blue-700' : 'text-gray-900'}`}>
        {value}
      </div>
    </Tag>
  );
};

export const MainBuyerPaymentPlan = () => {
  const dispatch = useAppDispatch();
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { selectedMainBuyer, selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);
  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);
  const { mainBuyerPaymentPlans, loadingList } = useAppSelector((state) => state.paymentPlans);
  const { paymentMethods } = useAppSelector((state) => state.payments);

  const [historyModalPlanId, setHistoryModalPlanId] = useState<string | null>(null);
  const [payingPlanId, setPayingPlanId] = useState<string | null>(null);
  const [invoicesToBePaid, setInvoicesToBePaid] = useState<QuickbooksInvoice[]>([]);
  const [selectedConfirmMethodByPlanId, setSelectedConfirmMethodByPlanId] = useState<
    Record<string, string>
  >({});
  const [agreedToAuthorizationByPlanId, setAgreedToAuthorizationByPlanId] = useState<
    Record<string, boolean>
  >({});
  const [changingMethodPlanId, setChangingMethodPlanId] = useState<string | null>(null);
  const [selectedChangeMethodId, setSelectedChangeMethodId] = useState('');
  // Active plan cards start collapsed to a minimal summary row - expanding is per-card, and
  // nothing is expanded by default.
  const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());
  const [collapsedScheduleIds, setCollapsedScheduleIds] = useState<Set<string>>(new Set());

  const isCancelling = loadingList.includes('cancelMainBuyerPaymentPlan');
  const isConfirming = loadingList.includes('confirmMainBuyerPaymentPlan');
  const isUpdatingPaymentMethod = loadingList.includes('updateMainBuyerPaymentPlanPaymentMethod');

  const buyerId = selectedSupplierForBuyer?.buyer?.id || '';

  const activePaymentMethodOptions = useMemo(
    () =>
      paymentMethods
        .filter((method) => !method.isDeactivated)
        .map((method) => ({ label: method.paymentMethodName ?? '', value: method.id })),
    [paymentMethods]
  );

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getGroupedBuyersSuppliers({ mainBuyerId: selectedMainBuyerId, weeksLimit: 2 }));
    }
  }, [selectedMainBuyerId, dispatch]);

  useEffect(() => {
    if (!selectedSupplierForBuyer && selectedBuyerSuppliers.length > 0) {
      dispatch(updateSelectedSupplierForBuyer(selectedBuyerSuppliers[0]));
    }
  }, [selectedBuyerSuppliers, selectedSupplierForBuyer, dispatch]);

  useEffect(() => {
    if (selectedMainBuyerId && buyerId) {
      dispatch(getMainBuyerPaymentPlans({ mainBuyerId: selectedMainBuyerId, buyerId }));
    }
  }, [selectedMainBuyerId, buyerId, dispatch]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId }));
    }
  }, [selectedMainBuyerId, dispatch]);

  // The selected supplier already carries its own live invoice buckets (fetched via
  // getGroupedBuyersSuppliers), so no extra request is needed to find what a plan's "Pay"
  // button should auto-select.
  const openInvoices: QuickbooksInvoice[] = useMemo(() => {
    const pastDue = selectedSupplierForBuyer?.invoices?.pastDueInvoices?.invoices ?? [];
    const current = selectedSupplierForBuyer?.invoices?.currentInvoices?.invoices ?? [];
    return [...pastDue, ...current].filter((invoice) => (invoice.balance ?? 0) > 0);
  }, [selectedSupplierForBuyer]);

  const pendingPlans = useMemo(
    () => mainBuyerPaymentPlans.filter((plan) => plan.status === 'PENDING_CONFIRMATION'),
    [mainBuyerPaymentPlans]
  );

  const activePlans = useMemo(
    () => mainBuyerPaymentPlans.filter((plan) => plan.status === 'ACTIVE'),
    [mainBuyerPaymentPlans]
  );

  const pastPlans = useMemo(
    () =>
      mainBuyerPaymentPlans.filter(
        (plan) => plan.status === 'COMPLETED' || plan.status === 'CANCELLED'
      ),
    [mainBuyerPaymentPlans]
  );

  const historyModalPlan = mainBuyerPaymentPlans.find((plan) => plan.id === historyModalPlanId);

  const handleCancelPlan = async (paymentPlanId: string) => {
    if (!selectedMainBuyerId || !buyerId) return;
    await dispatch(
      cancelMainBuyerPaymentPlan({ mainBuyerId: selectedMainBuyerId, buyerId, paymentPlanId })
    );
    dispatch(getMainBuyerPaymentPlans({ mainBuyerId: selectedMainBuyerId, buyerId }));
  };

  const handleConfirmPlan = async (paymentPlanId: string) => {
    const paymentMethodId = selectedConfirmMethodByPlanId[paymentPlanId];
    if (!selectedMainBuyerId || !buyerId || !paymentMethodId) {
      openDillToast({ message: 'Select a payment method to confirm this plan.', type: 'ERROR' });
      return;
    }
    if (!agreedToAuthorizationByPlanId[paymentPlanId]) {
      openDillToast({
        message: 'Please authorize automatic charges before confirming this plan.',
        type: 'ERROR',
      });
      return;
    }
    const result = await dispatch(
      confirmMainBuyerPaymentPlan({
        mainBuyerId: selectedMainBuyerId,
        buyerId,
        paymentPlanId,
        paymentMethodId,
      })
    );
    if (result.type === 'paymentPlans/confirmMainBuyerPaymentPlan/fulfilled') {
      const payload = result.payload as { success?: boolean } | null;
      if (payload?.success) {
        openDillToast({ message: 'Payment plan confirmed.', type: 'SUCCESS' });
        return;
      }
    }
    openDillToast({
      message: (result.payload as string) || 'Failed to confirm payment plan.',
      type: 'ERROR',
    });
  };

  const handleChangePaymentMethod = async (paymentPlanId: string) => {
    if (!selectedMainBuyerId || !buyerId || !selectedChangeMethodId) return;
    const result = await dispatch(
      updateMainBuyerPaymentPlanPaymentMethod({
        mainBuyerId: selectedMainBuyerId,
        buyerId,
        paymentPlanId,
        paymentMethodId: selectedChangeMethodId,
      })
    );
    if (result.type === 'paymentPlans/updateMainBuyerPaymentPlanPaymentMethod/fulfilled') {
      const payload = result.payload as { success?: boolean } | null;
      if (payload?.success) {
        openDillToast({ message: 'Payment method updated.', type: 'SUCCESS' });
        setChangingMethodPlanId(null);
        setSelectedChangeMethodId('');
        return;
      }
    }
    openDillToast({
      message: (result.payload as string) || 'Failed to update payment method.',
      type: 'ERROR',
    });
  };

  const handlePayNow = (plan: (typeof activePlans)[number]) => {
    const linkedIds =
      plan.linkedInvoiceIds && plan.linkedInvoiceIds.length > 0 ? plan.linkedInvoiceIds : null;
    const matchedInvoices = linkedIds
      ? openInvoices.filter(
          (invoice) =>
            (invoice.id && linkedIds.includes(invoice.id)) ||
            (invoice.platformInvoiceId && linkedIds.includes(invoice.platformInvoiceId))
        )
      : openInvoices;

    setPayingPlanId(plan.id);
    setInvoicesToBePaid(matchedInvoices);
  };

  const toggleExpandedPlan = (planId: string) => {
    setExpandedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  };

  const toggleScheduleCollapsed = (planId: string) => {
    setCollapsedScheduleIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  };

  const handlePaymentSuccess = () => {
    setPayingPlanId(null);
    setInvoicesToBePaid([]);
    if (selectedMainBuyerId && buyerId) {
      dispatch(getMainBuyerPaymentPlans({ mainBuyerId: selectedMainBuyerId, buyerId }));
    }
  };

  const renderPendingPlanCard = (plan: (typeof pendingPlans)[number]) => {
    const occurrenceText = formatPaymentPlanOccurrence(plan);
    const occurrenceTextLower = occurrenceText.charAt(0).toLowerCase() + occurrenceText.slice(1);
    const distributorName = selectedSupplierForBuyer?.name || 'your distributor';
    const isAgreed = !!agreedToAuthorizationByPlanId[plan.id];
    const installments = generatePaymentPlanInstallments(plan);
    const isScheduleExpanded = !collapsedScheduleIds.has(plan.id);

    return (
      <div key={plan.id} className="rounded-2xl border border-gray-200 shadow-sm bg-white p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">
            <CalendarBlank size={12} weight="fill" />
            Confirmation needed
          </span>
        </div>

        <p className="text-xs text-gray-600 mb-4 leading-relaxed">
          {formatMoney(plan.amountPerPayment, 2)} {occurrenceText} until{' '}
          {formatMoney(plan.totalAmount, 2)} is paid off. Pick a payment method below and confirm -
          once confirmed, each installment is charged automatically, so there is nothing more to do.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <StatTile label="Total balance" value={formatMoney(plan.totalAmount, 2)} accent />
          <StatTile label="Amount / cycle" value={formatMoney(plan.amountPerPayment, 2)} />
        </div>

        {installments.length > 0 && (
          <div className="mb-4 rounded-xl border border-gray-100 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleScheduleCollapsed(plan.id)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
              <span className="text-2xs font-semibold text-gray-600">
                View payment schedule ({installments.length}{' '}
                {installments.length === 1 ? 'installment' : 'installments'})
              </span>
              {isScheduleExpanded ? (
                <CaretUp size={12} color={COLORS.GREY_500} />
              ) : (
                <CaretDown size={12} color={COLORS.GREY_500} />
              )}
            </button>
            {isScheduleExpanded && (
              <div className="max-h-48 overflow-y-auto">
                {installments.map((installment) => (
                  <div
                    key={installment.installmentNumber}
                    className="flex items-center justify-between px-3 py-2 text-xs border-t border-gray-100">
                    <span className="text-gray-500">
                      #{installment.installmentNumber} ·{' '}
                      {moment(installment.date).format('MMM DD, YYYY')}
                    </span>
                    <span className="font-medium text-gray-900">
                      {formatMoney(installment.amount, 2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-4 max-w-xs">
          <DillField label="Payment method">
            <DillSelect
              value={selectedConfirmMethodByPlanId[plan.id] || ''}
              onChange={(value) =>
                setSelectedConfirmMethodByPlanId((prev) => ({ ...prev, [plan.id]: value }))
              }
              options={activePaymentMethodOptions}
              placeholder="Select a payment method"
            />
          </DillField>
          {activePaymentMethodOptions.length === 0 && (
            <p className="text-2xs text-gray-400 mt-1.5">
              Add a payment method in the Payment Methods panel first.
            </p>
          )}
        </div>

        <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
          <DillCheckbox
            isChecked={isAgreed}
            onChange={(checked: boolean) =>
              setAgreedToAuthorizationByPlanId((prev) => ({ ...prev, [plan.id]: checked }))
            }
            className="mt-0.5"
          />
          <span className="text-2xs text-gray-500 leading-relaxed">
            By clicking &ldquo;Confirm Plan&rdquo;, I authorize {distributorName} to automatically
            charge my selected payment method {formatMoney(plan.amountPerPayment, 2)}{' '}
            {occurrenceTextLower} until the {formatMoney(plan.totalAmount, 2)} balance is paid in
            full. You can update your payment method any time by clicking &ldquo;Change&rdquo; on
            the linked payment method. If you&rsquo;d like to cancel, please reach out to your
            distributor.
          </span>
        </label>

        <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
          <DillButton
            label="Confirm Plan"
            onClick={() => handleConfirmPlan(plan.id)}
            isDisabled={isConfirming || !selectedConfirmMethodByPlanId[plan.id] || !isAgreed}
            height={38}
          />
          <DillButton
            label="Decline"
            variant="secondary"
            onClick={() => handleCancelPlan(plan.id)}
            isDisabled={isCancelling}
            height={38}
          />
        </div>
      </div>
    );
  };

  const renderPlanCard = (plan: (typeof activePlans)[number]) => {
    const progressPct = Math.min(
      100,
      ((plan.totalAmount - plan.remainingBalance) / plan.totalAmount) * 100
    );
    const linkedPaymentMethod = paymentMethods.find((method) => method.id === plan.paymentMethodId);
    const isChangingMethod = changingMethodPlanId === plan.id;
    const isExpanded = expandedPlanIds.has(plan.id);

    return (
      <div
        key={plan.id}
        className="rounded-2xl border border-gray-200 shadow-sm bg-white mb-5 overflow-hidden">
        <button
          type="button"
          onClick={() => toggleExpandedPlan(plan.id)}
          className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50 transition-colors">
          <div className="min-w-0">
            <div className="mb-1.5 flex items-baseline gap-1.5">
              <span className="text-xl font-bold text-gray-900">
                {formatMoney(plan.remainingBalance, 2)}
              </span>
              <span className="text-xs font-medium text-gray-400">remaining</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">
                <CheckCircle size={12} weight="fill" />
                Active
              </span>
              {plan.isOverdue && (
                <span className="inline-flex items-center gap-1 text-2xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                  <Warning size={12} weight="fill" />
                  Overdue
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                <Repeat size={11} />
                {formatMoney(plan.amountPerPayment, 2)}/cycle
              </span>
              <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                <CalendarBlank size={11} />
                Next due {moment(plan.nextDueDate).format('MMM DD, YYYY')}
              </span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-2xs font-medium text-gray-600 border border-gray-200 rounded-full px-2.5 py-1 shrink-0">
            Details
            {isExpanded ? (
              <CaretUp size={12} color={COLORS.GREY_500} />
            ) : (
              <CaretDown size={12} color={COLORS.GREY_500} />
            )}
          </span>
        </button>

        <div className="px-5 pb-4 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-2xs font-medium text-gray-500 shrink-0">
            {Math.round(progressPct)}% paid off
          </span>
        </div>

        {isExpanded && (
          <div className="px-5 pb-5">
            {plan.linkedInvoiceIds && plan.linkedInvoiceIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(plan.references || []).map((ref, i) => (
                  <span
                    key={`${ref.id}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                    {ref.description || ref.id}
                  </span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-4 pt-4 border-t border-gray-100">
              <StatTile
                label="Remaining balance"
                value={formatMoney(plan.remainingBalance, 2)}
                accent
              />
              <StatTile label="Amount / cycle" value={formatMoney(plan.amountPerPayment, 2)} />
              <StatTile label="Payments made" value={`${plan.paymentsMadeCount}`} />
            </div>

            <div className="mb-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-2xs font-medium text-gray-500">Auto-charging from</span>
                {!isChangingMethod && (
                  <button
                    type="button"
                    onClick={() => {
                      setChangingMethodPlanId(plan.id);
                      setSelectedChangeMethodId(plan.paymentMethodId || '');
                    }}
                    className="text-2xs font-medium text-blue-600 hover:text-blue-700">
                    Change
                  </button>
                )}
              </div>
              {isChangingMethod ? (
                <div className="flex items-end gap-2">
                  <div className="flex-1 max-w-[220px]">
                    <DillSelect
                      value={selectedChangeMethodId}
                      onChange={setSelectedChangeMethodId}
                      options={activePaymentMethodOptions}
                      placeholder="Select a payment method"
                    />
                  </div>
                  <DillButton
                    label="Save"
                    onClick={() => handleChangePaymentMethod(plan.id)}
                    isDisabled={isUpdatingPaymentMethod || !selectedChangeMethodId}
                    height={38}
                  />
                  <DillButton
                    label="Cancel"
                    variant="secondary"
                    onClick={() => {
                      setChangingMethodPlanId(null);
                      setSelectedChangeMethodId('');
                    }}
                    height={38}
                  />
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-900">
                  {linkedPaymentMethod?.paymentMethodName || 'No payment method on this plan'}
                </span>
              )}
            </div>

            {plan.confirmedAt && (
              <div className="mb-4 pt-4 border-t border-gray-100">
                <span className="text-2xs font-medium text-gray-500 block mb-1">Authorization</span>
                <p className="text-2xs text-gray-400 leading-relaxed">
                  Confirmed {formatPaymentPlanConfirmationTimestamp(plan.confirmedAt)}
                  {plan.confirmedByEmail ? ` by ${plan.confirmedByEmail}` : ''}
                  {plan.confirmedByIp ? ` from IP ${plan.confirmedByIp}` : ''}.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-4 pb-4 border-t border-gray-100">
              <DillButton
                label="Pay Early"
                icon={<CreditCard size={14} />}
                variant="secondary"
                onClick={() => handlePayNow(plan)}
                height={38}
              />
            </div>

            <div className="pt-4 border-t border-gray-100">
              <span className="text-2xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                Payment history
              </span>
              <PaymentPlanHistoryList
                paymentHistory={plan.paymentHistory}
                missedPayments={plan.missedPayments}
                failedCharges={plan.failedCharges}
                accountType="buyer"
                maxHeightClassName="max-h-60"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full p-4 pb-8 overflow-y-auto">
      <div className="w-full">
        <p className="text-xs text-gray-500 mb-5 max-w-lg leading-relaxed">
          Payment plans set up by your supplier. Confirm a new plan and link a payment method once -
          after that, each installment is charged automatically, so there is nothing more to do.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 min-w-0">
            {selectedBuyerSuppliers.length > 1 && (
              <div className="mb-5 max-w-xs">
                <DillField label="Supplier">
                  <DillSelect
                    value={selectedSupplierForBuyer?.id || ''}
                    onChange={(supplierId) => {
                      const supplier = selectedBuyerSuppliers.find((s) => s.id === supplierId);
                      if (supplier) dispatch(updateSelectedSupplierForBuyer(supplier));
                    }}
                    options={selectedBuyerSuppliers.map((s) => ({
                      label: s.name || '',
                      value: s.id,
                    }))}
                  />
                </DillField>
              </div>
            )}

            {pendingPlans.map(renderPendingPlanCard)}

            {pendingPlans.length === 0 && activePlans.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white p-8 mb-5 text-center">
                <p className="text-xs text-gray-400">No active payment plans from this supplier.</p>
              </div>
            ) : (
              activePlans.map(renderPlanCard)
            )}

            {pastPlans.length > 0 && (
              <div className="mb-5">
                <span className="text-2xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Plan history
                </span>
                <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
                  <div className="grid grid-cols-4 px-4 py-2.5 text-2xs font-semibold text-gray-500 bg-gray-50">
                    <span>Created</span>
                    <span>Amount</span>
                    <span>Status</span>
                    <span>Payments made</span>
                  </div>
                  {pastPlans.map((plan) => (
                    <button
                      type="button"
                      key={plan.id}
                      onClick={() => setHistoryModalPlanId(plan.id)}
                      className="grid grid-cols-4 px-4 py-2.5 text-xs items-center border-t border-gray-100 w-full text-left hover:bg-gray-50">
                      <span>
                        {plan.createdAt ? moment(plan.createdAt).format('MMM DD, YYYY') : ''}
                      </span>
                      <span className="font-medium">{formatMoney(plan.totalAmount, 2)}</span>
                      <span>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            plan.status === 'COMPLETED'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                          {plan.status === 'COMPLETED' ? 'Completed' : 'Cancelled'}
                        </span>
                      </span>
                      <span>{plan.paymentsMadeCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1 min-w-0">
            <BuyerPaymentMethods layout="column" />
          </div>
        </div>
      </div>

      <PaymentPlanHistoryModal
        open={!!historyModalPlanId}
        onClose={() => setHistoryModalPlanId(null)}
        paymentHistory={historyModalPlan?.paymentHistory}
        missedPayments={historyModalPlan?.missedPayments}
        failedCharges={historyModalPlan?.failedCharges}
        accountType="buyer"
      />

      <AppSelectedPayInvoicesModal
        open={!!payingPlanId}
        title="Pay Payment Plan"
        handleClose={() => {
          setPayingPlanId(null);
          setInvoicesToBePaid([]);
        }}
        selectedInvoices={invoicesToBePaid}
        selectedCreditMemos={[]}
        paymentTo={selectedSupplierForBuyer?.name?.toLocaleLowerCase()}
        paymentFrom={selectedMainBuyer?.name?.toLocaleLowerCase()}
        paymentPlanId={payingPlanId ?? undefined}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default MainBuyerPaymentPlan;
